import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <article className="group relative rounded-lg overflow-hidden bg-background-50 border border-background-200/60 hover:border-primary-300/50 transition-all duration-300">
        <Link to={`/article/${article.slug}`} className="block">
          <div className="relative h-64 overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - 吃瓜黑料在线`}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-5">
              {article.isHot && (
                <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-500 text-background-50 mb-3">
                  热门
                </span>
              )}
              <h3 className="text-lg md:text-xl font-heading font-bold text-white leading-snug line-clamp-2 mb-2">
                {article.title}
              </h3>
              <p className="text-sm text-white/75 line-clamp-2">{article.excerpt}</p>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  if (variant === 'compact') {
    return (
      <article className="group flex gap-3 py-3 border-b border-background-200/50 last:border-b-0">
        <Link to={`/article/${article.slug}`} className="shrink-0">
          <div className="w-20 h-20 rounded-lg overflow-hidden bg-background-100">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - 吃瓜黑料在线`}
              className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
            />
          </div>
        </Link>
        <div className="flex flex-col justify-between min-w-0 flex-1">
          <Link to={`/article/${article.slug}`}>
            <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
              {article.title}
            </h4>
          </Link>
          <div className="flex items-center gap-3 text-xs text-foreground-500 mt-1">
            <span className="flex items-center gap-1">
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-eye-line"></i>
              </div>
              {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
            </span>
            <span>{article.publishDate}</span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group bg-background-50 rounded-lg border border-background-200/60 hover:border-primary-300/50 transition-all duration-300 overflow-hidden">
      <Link to={`/article/${article.slug}`}>
        <div className="relative h-48 overflow-hidden">
          <img
            src={article.image}
            alt={article.title}
            title={`${article.title} - 吃瓜黑料在线`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute top-3 left-3 flex gap-1.5 flex-wrap">
            {article.isHot && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-accent-500 text-background-50">
                热门
              </span>
            )}
            {article.isNew && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary-500 text-background-50">
                最新
              </span>
            )}
          </div>
          <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-xs bg-black/50 text-white backdrop-blur-sm">
            {article.category}
          </span>
        </div>
      </Link>
      <div className="p-4">
        <Link to={`/article/${article.slug}`}>
          <h3 className="text-base font-heading font-bold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug mb-2">
            {article.title}
          </h3>
        </Link>
        <p className="text-sm text-foreground-600 line-clamp-2 mb-3 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-eye-line"></i>
              </div>
              {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
            </span>
            <span className="flex items-center gap-1">
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-time-line"></i>
              </div>
              {article.publishDate}
            </span>
          </div>
          <Link
            to={`/category/${article.categorySlug}`}
            className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
          >
            {article.category}
          </Link>
        </div>
      </div>
    </article>
  );
}